class VariantRadios extends VariantSelects {
  constructor() {
    super();
    this.updateOptions();
    this.availableOptionCombinations = this.buildAvailabilityMap();
    this.updateVariantAvailability();
    this.addEventListener('change', this.onRadioChange.bind(this));
  }

  onRadioChange(event) {
    const seatSelector = this.querySelector('#main-product.content-section fieldset.seats');
    const isSeatNumberChange = seatSelector?.contains(event.target) || false;
    const productSelector = this.querySelector('#main-product.content-section fieldset.sectionals');
    const isProductChange = productSelector?.contains(event.target) || false;
    const layoutSelector = this.querySelector('#main-product.content-section');
    const isLayoutChange = layoutSelector?.contains(event.target) || false;
    const modelSelector = this.querySelector('#model.content-section fieldset');
    const isModelChange = modelSelector?.contains(event.target) || false;

    if (isSeatNumberChange) {
      this.updateSeatsDescription(event.target);
      this.updateSectionals(event.target.value);
      const selectedSectional = this.getSelectedSectional();
      const productId = selectedSectional.dataset.productId;
      const selectedVariant = this.getVariantFromProduct(productId);
      this.showProduct(productId, selectedSectional.dataset.url, selectedVariant.id);
    }
    if (isProductChange) {
      const productId = event.target.dataset.productId;
      const selectedVariant = this.getVariantFromProduct(productId);
      this.updateSectionalsText(event.target);
      this.showProduct(productId, event.target.dataset.url, selectedVariant.id);
    }
    if (isLayoutChange) {
      this.updateCurrentProductId(true);
    }
    // currentProductId is only ever set by showProduct(), i.e. on multi-product
    // (sectional) configurators. Gating on it meant single-product configurators never
    // refreshed their Model cards, so those cards kept whatever the server rendered.
    // updateOptions() has to run first: updateModelPrices reads this.options to find the
    // variants matching the current combination.
    this.updateOptions();
    if (!isModelChange) {
      this.updateModelPrices();
    }
    if (this.currentVariant) {
      this.updateStickyHeaderTitle();
      this.updateStickyHeaderPrice();
      this.updateStickyHeaderImage();
    }
    if (!this.currentProductId) {
      this.updateCheckoutLabel();
    }
    this.availableOptionCombinations = this.buildAvailabilityMap(this.currentProductId);
    this.updateVariantAvailability();
  }

  getVariantFromProduct(productId) {
    // Get the current options for the newly selected product
    const variantsList = this.getVariantData(productId);
    let selectedVariant = variantsList.find((variant) => {
      return (
        variant.available &&
        !variant.options
          .map((option, index) => {
            return this.options[index] === option;
          })
          .includes(false)
      );
    });
    // If unavailable, select the first available variant
    if (!selectedVariant) {
      selectedVariant = variantsList.find((variant) => variant.available);
      const fieldsets = Array.from(this.querySelectorAll('fieldset:not(.seats, .sectionals)'));
      fieldsets.forEach((fieldset, index) => {
        return Array.from(fieldset.querySelectorAll('input')).forEach((radio) => {
          if (radio.value == selectedVariant.options[index]) {
            radio.checked = true;
          } else {
            radio.checked = false;
          }
        });
      });
    }
    if (productId == this.currentProductId) {
      this.currentVariant = selectedVariant;
    }
    return selectedVariant;
  }

  getSelectedSectional() {
    const visibleSectional = this.querySelector('.sectionals form:not(.hidden)');
    let selectedSectional = visibleSectional.querySelector('input:checked') || visibleSectional.querySelector('input');
    selectedSectional.checked = true;
    return selectedSectional;
  }

  updateSeatsDescription(target) {
    const seatsDescription = target.dataset.description || '',
      formLabel = this.querySelector('.form__label #selected-number-of-seats-description');
    formLabel && (formLabel.innerHTML = seatsDescription);
  }

  updateSectionalsText(target) {
    const sectionalsText = target.dataset.sectionalsText || '',
      formLabel = this.querySelector('.form__label #selected-sectionals-text');
    formLabel && (formLabel.innerHTML = sectionalsText);
  }

  updateSectionals(value) {
    const hiddenSectionals = this.querySelectorAll(`.sectionals form:not(#seats-${value})`);
    const visibleSectional = this.querySelector(`#seats-${value}`);
    (hiddenSectionals.forEach((element) => element.classList.add('hidden')),
      visibleSectional.classList.remove('hidden'));
    let checkedSectional = visibleSectional.querySelector('input:checked');
    if (!checkedSectional) {
      checkedSectional = visibleSectional.querySelector('input');
      checkedSectional.checked = true;
    }
    this.updateSectionalsText(checkedSectional);
  }

  showProduct(productId, url, variantId) {
    this.currentProductId = productId;
    this.dataset.url = url;
    const productUrl = variantId ? `${url}?variant=${variantId}` : url;
    window.history.replaceState({}, '', withConfiguratorView(productUrl));

    // Reveal the target gallery and hide the rest. Guarded so a reveal that was
    // waiting on a just-in-time fetch can't clobber a newer selection.
    const reveal = () => {
      if (this.currentProductId != productId) return;
      document.querySelectorAll('.product__media-gallery').forEach((mediaGallery) => {
        mediaGallery.classList.toggle('hidden', mediaGallery.id != `MediaGallery-${productId}`);
      });
    };

    if (document.getElementById(`MediaGallery-${productId}`)) {
      // Already loaded (the common case, warmed in the background) — instant swap.
      reveal();
    } else {
      // Not warmed yet: fetch just this config's gallery now, keeping the current
      // gallery on screen until it arrives so the switch never blanks out or reloads.
      ensureGalleryLoaded(productId, url).then(() => {
        if (this.currentProductId != productId) return; // superseded by a newer pick
        reveal();
        // onVariantChange already ran without the gallery present; sync the freshly
        // injected slides to the selected variant.
        this.filterMedia();
        this.updateMedia();
      });
    }

    this.onVariantChange();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset:not(.seats, .sectionals)'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }

  updateModelPrices() {
    const modelFieldSet = this.querySelector('#model fieldset');
    if (!modelFieldSet) return;
    // Index of the "Model" option in the configurator;
    const modelOptionIndex = parseInt(modelFieldSet.dataset.optionIndex || 2);
    if (!modelFieldSet) return;
    const currentVariants = this.getVariantData(this.currentProductId);
    const modelVariants = currentVariants.filter((variant) => {
      return !variant.options
        .toSpliced(modelOptionIndex, 1)
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
    modelVariants.forEach((modelVariant) => {
      const optionPriceLabel = modelFieldSet.querySelector(
        `.product-form__input-wrapper [value="${modelVariant.options[modelOptionIndex]}"]+label .price-wrapper.price__container`,
      );
      if (!optionPriceLabel) return;
      optionPriceLabel.dataset.price = modelVariant.price;
      optionPriceLabel.dataset.comparedAt = modelVariant.compare_at_price || '';
      // `|| '' === 'true'` parsed as `dataset.currencyCodeEnabled || false`, so the string
      // "false" came back truthy and the currency code was always appended.
      const currencyCodeEnabled = optionPriceLabel.dataset.currencyCodeEnabled === 'true';
      const currentPriceElement = optionPriceLabel.querySelector('.current-price');
      if (currentPriceElement) {
        currentPriceElement.innerHTML = currencyCodeEnabled
          ? formatWithCurrency(modelVariant.price)
          : formatPrice(modelVariant.price);
      }
      const compareAtPriceElement = optionPriceLabel.querySelector('.compare-at');
      if (compareAtPriceElement) {
        // Same guard as the Liquid and snippets/price.liquid: only strike through on a
        // real discount, never render compare_at when it's absent or equal to the price.
        const modelOnSale = modelVariant.compare_at_price && modelVariant.compare_at_price > modelVariant.price;
        if (modelOnSale) {
          compareAtPriceElement.innerHTML = currencyCodeEnabled
            ? formatWithCurrency(modelVariant.compare_at_price)
            : formatPrice(modelVariant.compare_at_price);
        }
        compareAtPriceElement.style.display = modelOnSale ? '' : 'none';
      }
    });
  }

  buildAvailabilityMap(productId) {
    // Options availability map based on currently selected options
    const variantData = this.getVariantData(productId);
    if (!variantData) return new Map();
    const availabilityMap = new Map();
    const selectedOptions = this.options || this.currentVariant.options;
    selectedOptions.forEach((selectedOption, selectedOptionIndex) => {
      if (!availabilityMap.has(selectedOptionIndex)) {
        availabilityMap.set(selectedOptionIndex, new Map());
      }
      const adjacentVariants = variantData.filter((variant) =>
        variant.options.every((option, index) => {
          if (selectedOptionIndex == index) {
            return selectedOptions[index] !== option;
          } else {
            return selectedOptions[index] == option;
          }
        }),
      );
      const optionMap = availabilityMap.get(selectedOptionIndex);
      adjacentVariants.forEach((adjacentVariant) => {
        const key = adjacentVariant.options[selectedOptionIndex];
        if (optionMap.has(key)) {
          optionMap.set(key, optionMap.get(key) || adjacentVariant.available);
        } else {
          optionMap.set(key, adjacentVariant.available);
        }
      });
    });
    return availabilityMap;
  }

  updateVariantAvailability() {
    if (!this.availableOptionCombinations) return;

    const fieldsets = Array.from(this.querySelectorAll('fieldset:not(.seats, .sectionals)'));
    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const radios = Array.from(fieldset.querySelectorAll("input[type='radio']"));
      const optionMap = this.availableOptionCombinations.get(fieldsetIndex);
      if (!optionMap) return;
      radios.forEach((radio) => {
        const optionValue = radio.value;
        const isAvailable = optionMap.get(optionValue);
        if (isAvailable === false) {
          radio.setAttribute('disabled', '');
          radio.nextElementSibling.classList.add('sold-out');
        } else {
          radio.removeAttribute('disabled');
          radio.nextElementSibling.classList.remove('sold-out');
        }
      });
    });
  }

  updateCheckoutLabel() {
    if (!this.currentVariant) return;

    // The availability line was moved out of <variant-radios> so it can sit under the card's
    // Add to Cart button, so scope the lookup to the whole card rather than to this element.
    const labelElement = this.closest('.product-wrapper')?.querySelector('.addon-availability-text');
    if (!labelElement) return;

    const availabilityText = this.currentVariant.availability_text;

    if (labelElement) {
      labelElement.dataset.isPreorder = this.currentVariant.is_preorder;
      labelElement.textContent = availabilityText;
      labelElement.style.display = 'block';
    }
  }

  updateStickyHeaderTitle() {
    // Update mobile sticky header product name when variant changes
    const mobileHeaderTitle = document.getElementById(`MobileHeaderTitle-${this.dataset.section}`);
    if (mobileHeaderTitle && this.currentVariant.name) {
      const lastIndex = String(this.currentVariant.name).lastIndexOf(' - ');
      const productName = String(this.currentVariant.name.slice(0, lastIndex));
      mobileHeaderTitle.textContent = productName;
    }
  }

  updateStickyHeaderPrice() {
    // Update mobile sticky header price when variant changes
    const mobileHeaderPrice = document.querySelector(`.mobile-sticky-header__details > .mobile-sticky-header__price`);

    if (mobileHeaderPrice) {
      // The header mirrors the configured total (add-ons included) rather than the bare
      // variant price, so it can't disagree with the Total block. syncConfiguratorPromoFromDom
      // owns the header's price nodes and its price--on-sale class; this runs after the
      // parent handler has refreshed the Total's data attributes, so it reads current values.
      if (typeof window.syncConfiguratorPromoFromDom === 'function') {
        window.syncConfiguratorPromoFromDom();
      } else {
        const regularPriceElement = mobileHeaderPrice.querySelector('.price__regular .price-item--regular');
        const salePriceElement = mobileHeaderPrice.querySelector('.price__sale .price-item--sale');
        const compareAtPriceElement = mobileHeaderPrice.querySelector('.price__sale .price-item--compare');
        const updatedSalePrice = this.currentVariant.price;
        const updatedComparePrice = this.currentVariant.compare_at_price;
        const isOnSale = updatedComparePrice > updatedSalePrice;

        if (isOnSale) {
          this.updateSalePrice(
            updatedSalePrice,
            updatedComparePrice,
            regularPriceElement,
            salePriceElement,
            compareAtPriceElement,
          );
        } else {
          this.updateRegularPrice(updatedSalePrice, regularPriceElement, salePriceElement, compareAtPriceElement);
        }
        mobileHeaderPrice.classList.toggle('price--on-sale', isOnSale);
      }

      mobileHeaderPrice.classList.remove('visibility-hidden');
    }
  }

  updateStickyHeaderImage() {
    // Update mobile sticky header image when variant changes
    const mobileHeaderImage = document.getElementById(`MobileHeaderImage-${this.dataset.section}`);
    if (mobileHeaderImage && this.currentVariant.featured_media) {
      const newSrc = this.currentVariant.featured_media.preview_image.src;
      if (newSrc) {
        // Update srcset for responsive images
        const imgWidth = this.currentVariant.featured_media.preview_image.width;
        let srcset = '';

        if (imgWidth >= 120) {
          srcset += `${this.currentVariant.featured_media.preview_image.src.replace(
            /\.(jpg|jpeg|png|gif|webp)/i,
            `_120x.$1`,
          )} 120w`;
        }

        if (imgWidth >= 240) {
          srcset += `, ${this.currentVariant.featured_media.preview_image.src.replace(
            /\.(jpg|jpeg|png|gif|webp)/i,
            `_240x.$1`,
          )} 240w`;
        }

        if (imgWidth >= 360) {
          srcset += `, ${this.currentVariant.featured_media.preview_image.src.replace(
            /\.(jpg|jpeg|png|gif|webp)/i,
            `_360x.$1`,
          )} 360w`;
        }

        mobileHeaderImage.srcset = srcset;
        mobileHeaderImage.src = this.currentVariant.featured_media.preview_image.src.replace(
          /\.(jpg|jpeg|png|gif|webp)/i,
          `_120x.$1`,
        );
        mobileHeaderImage.alt = this.currentVariant.featured_media.alt || this.currentVariant.title;
      }
    }
  }
}

customElements.define('variant-radios', VariantRadios);

// ─── Configurator gallery loading ────────────────────────────────────────────
// Each configurable product has its own <media-gallery> (~5.4 MB of parsed HTML,
// ~300 <img> elements). Fetching all 13 up front on DOMContentLoaded floods the
// network and blocks the main thread with ~70 MB of DOMParser work during the
// page-settle window. Instead, galleries are injected lazily: warmed in the
// background at idle after load, and fetched just-in-time if the user selects a
// config before its warm-up lands. showProduct() reveals whichever gallery is
// ready and only ever swaps — it never triggers a full page reload.

const galleryFetches = new Map(); // productId -> in-flight Promise (dedupes fetches)

function getLoadedGalleryProductIds() {
  return new Set(
    Array.from(document.querySelectorAll('.grid__item.product__media-wrapper .product__media-gallery')).map(
      (mediaGallery) => mediaGallery.dataset.productId,
    ),
  );
}

function injectConfiguratorGallery(productMediaGallery) {
  const parentDiv = document.querySelector('.grid__item.product__media-wrapper');
  if (!parentDiv) return;
  // Keep the "Dimensions & Specs" button as the last child so it always renders
  // after whichever gallery is visible. Appending galleries after the button
  // strands it above the image when a non-default config is active.
  const dimensionsButton = parentDiv.querySelector('.dimensions-button-wrapper');
  if (dimensionsButton) {
    parentDiv.insertBefore(productMediaGallery, dimensionsButton);
  } else {
    parentDiv.appendChild(productMediaGallery);
  }
}

// Ensure a config's gallery is in the DOM (hidden). Returns a Promise that resolves
// once it's injected. Safe to call repeatedly: resolves immediately if the gallery
// already exists, and shares a single fetch if one is already in flight.
function ensureGalleryLoaded(productId, url) {
  if (document.getElementById(`MediaGallery-${productId}`)) return Promise.resolve();
  if (galleryFetches.has(productId)) return galleryFetches.get(productId);

  const request = fetch(withConfiguratorView(url), { priority: 'low' })
    .then((response) => response.text())
    .then((responseText) => {
      // A concurrent path may have injected it while we were fetching.
      if (document.getElementById(`MediaGallery-${productId}`)) return;
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const productMediaGallery = html.querySelector('media-gallery');
      if (!productMediaGallery) return;
      productMediaGallery.classList.add('hidden');
      injectConfiguratorGallery(productMediaGallery);
    })
    .catch(() => {
      // Drop the record so a later selection can retry after a transient failure.
      galleryFetches.delete(productId);
    });

  galleryFetches.set(productId, request);
  return request;
}

// Warm every config's gallery in the background, throttled and at idle, so the cost
// lands after first paint instead of competing with it. By the time the shopper
// switches configs the gallery is usually already in the DOM (instant swap); any
// not-yet-warmed config is covered just-in-time by showProduct().
function prefetchConfiguratorGalleries() {
  const seen = getLoadedGalleryProductIds();
  const queue = [];
  document
    .querySelectorAll('.sectionals .product-form__input-wrapper input[name="Sectionals"]')
    .forEach((input) => {
      const productId = input.dataset.productId;
      if (productId && !seen.has(productId)) {
        seen.add(productId);
        queue.push({ productId, url: input.dataset.url });
      }
    });

  let index = 0;
  let active = 0;
  const CONCURRENCY = 2;
  const idle = (cb) =>
    window.requestIdleCallback ? window.requestIdleCallback(cb, { timeout: 2000 }) : setTimeout(cb, 200);

  function pump() {
    while (active < CONCURRENCY && index < queue.length) {
      const { productId, url } = queue[index++];
      active++;
      ensureGalleryLoaded(productId, url).finally(() => {
        active--;
        if (index < queue.length) idle(pump);
      });
    }
  }
  pump();
}

function startConfiguratorPrefetch() {
  const kick = () =>
    window.requestIdleCallback
      ? window.requestIdleCallback(prefetchConfiguratorGalleries, { timeout: 3000 })
      : setTimeout(prefetchConfiguratorGalleries, 1200);
  // Wait for load so warm-up never competes with the page's own critical resources.
  if (document.readyState === 'complete') kick();
  else window.addEventListener('load', kick, { once: true });
}
startConfiguratorPrefetch();